import React, { useRef, useCallback } from 'react';
import { KiteCard, KiteAlert } from '@kite/react-kite';
import { ITCOForm, ITCOSubmission } from 'types';
import { useTCORenderFields } from 'hooks';
import Table, { TableRow } from './../TCOTableComponent';
import { useTcoGenerateProductTotals } from '../../../../hooks/useGenerateTcoProductTotals';
import './TCOOperationalCostGeneralInformation.scss';
import { tcoGeneralInformationFieldIds } from 'utils';

export interface ITCOGeneralInformationProps {
  /** Form data from query */
  form: ITCOForm;
  tcoSubmissions: ITCOSubmission[];
}

const TCOOperationalCostsGeneralInformation = ({
  form,
  tcoSubmissions,
}: ITCOGeneralInformationProps) => {
  const { name, fields, id } = form;
  const ref = useRef<HTMLFormElement>(null);

  const handleSubmit = useCallback((e: React.FormEvent<HTMLFormElement>) => {
    // could add manual save here if needed
    e.preventDefault();
  }, []);

  const { fieldComponents, updateTcoSubmissionsError } =
    useTCORenderFields({
      fields,
      tcoSubmissions,
      formId: id,
    });

  const {
    averageTermCostId,
  } = tcoGeneralInformationFieldIds;

  const { generateTcoProductTotals } = useTcoGenerateProductTotals();

  const tcoOutputTotals = generateTcoProductTotals({
    format: false,
  });

  const averageOverTerm = tcoSubmissions.find(s => s.fieldId === averageTermCostId)?.answer!;

  const spectrumRows: TableRow[] = [
    {
      label: 'Configuration',
      values: {
        edge: tcoOutputTotals.spectrumConfigEdgeOperationalCost,
        switch: tcoOutputTotals.spectrumConfigSwitchOperationalCost,
        ap: tcoOutputTotals.spectrumConfigApOperationalCost,
        camera: tcoOutputTotals.spectrumConfigCameraOperationalCost,
        total: tcoOutputTotals.spectrumConfigurationCostTotal,
      },
    },
    {
      label: 'Monitoring',
      values: {
        edge: tcoOutputTotals.spectrumMonitoringEdgeOperationalCost,
        switch: tcoOutputTotals.spectrumMonitoringSwitchOperationalCost,
        ap: tcoOutputTotals.spectrumMonitoringApOperationalCost,
        camera: tcoOutputTotals.spectrumMonitoringCameraOperationalCost,
        total: tcoOutputTotals.spectrumMonitoringCostTotal,
      },
    },
    {
      label: 'Troubleshooting',
      values: {
        edge: tcoOutputTotals.spectrumTroubleshootingEdgeOperationalCost,
        switch: tcoOutputTotals.spectrumTroubleshootingSwitchOperationalCost,
        ap: tcoOutputTotals.spectrumTroubleshootingApOperationalCost,
        camera: tcoOutputTotals.spectrumTroubleshootingCameraOperationalCost,
        total: tcoOutputTotals.spectrumTroubleshootingCostTotal,
      },
    },
  ];

  const diyRows: TableRow[] = [
    {
      label: 'Configuration',
      values: {
        edge: tcoOutputTotals.diyConfigEdgeOperationalCost,
        switch: tcoOutputTotals.diyConfigSwitchOperationalCost,
        ap: tcoOutputTotals.diyConfigApOperationalCost,
        camera: tcoOutputTotals.diyConfigCameraOperationalCost,
        total: tcoOutputTotals.diyConfigurationCostTotal,
      },
    },
    {
      label: 'Monitoring',
      values: {
        edge: tcoOutputTotals.diyMonitoringEdgeOperationalCost,
        switch: tcoOutputTotals.diyMonitoringSwitchOperationalCost,
        ap: tcoOutputTotals.diyMonitoringApOperationalCost,
        camera: tcoOutputTotals.diyMonitoringCameraOperationalCost,
        total: tcoOutputTotals.diyMonitoringCostTotal,
      },
    },
    {
      label: 'Troubleshooting',
      values: {
        edge: tcoOutputTotals.diyTroubleshootingEdgeOperationalCost,
        switch: tcoOutputTotals.diyTroubleshootingSwitchOperationalCost,
        ap: tcoOutputTotals.diyTroubleshootingApOperationalCost,
        camera: tcoOutputTotals.diyTroubleshootingCameraOperationalCost,
        total: tcoOutputTotals.diyTroubleshootingCostTotal,
      },
    },
  ];

  return (
    <div className="kitecards-container">
      <KiteCard className="tco-general-information">
        <div className="tco-general-information__title">{name}</div>
        <div className="Operational-costs">
          {updateTcoSubmissionsError && (
            <KiteAlert
              type="alert"
              title="Save Error"
              description="Something went wrong saving your answers. Any changes will not be saved. Please refresh and try again later."
            />
          )}
          <form
            ref={ref}
            onSubmit={handleSubmit}
            className="operational-costs__row"
          >
            {fieldComponents.slice(0, -1)}
            <div className="average-value-label">
              Average Over Term
              <div className="average-value">
                {!isNaN(Number(averageOverTerm))
                  ? '$' + averageOverTerm
                  : averageOverTerm}
              </div>
            </div>
          </form>
        </div>
      </KiteCard>

      <div className="kitecards-container_spec">
        <KiteCard className="kitecard-spectrum">
          <div className="tco-general-information__title_spectrum">
            Spectrum
          </div>
          <Table rows={spectrumRows} />
        </KiteCard>
      </div>
      <div className="kitecards-container_diy">
        <KiteCard className="kitecard-diy">
          <div className="tco-general-information__title_diy">DIY</div>
          <Table rows={diyRows} />
        </KiteCard>
      </div>
    </div>
  );
};

export default TCOOperationalCostsGeneralInformation;
